import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { MARK } from "./chartTheme";
import type { ShareDonutChartProps, ShareSegment } from "./contract";
import styles from "./ShareDonutChart.module.css";

// 비율을 사람이 읽는 정수 퍼센트로 바꾼다. 0 보다 큰데 반올림하면 0 이 되는 조각은 1% 로 올린다.
// 그래서 "있는데 0%" 로 읽히지 않는다.
function percentText(share: number): string {
  const percent = Math.round(share * 100);
  return `${share > 0 && percent === 0 ? 1 : percent}%`;
}

function segmentTitle(segment: ShareSegment, formatCount: (count: number) => string): string {
  return `${segment.label} ${formatCount(segment.count)} (${percentText(segment.share)})`;
}

/** 링의 높이(px). 축 하나를 제목·링·범례로 완결해서 보여 주는 최소 크기다. */
const RING_HEIGHT = 140;

// 한 축의 부분-전체를 도넛 하나로 보여 준다. 조각이 2~4개인 축 전용이다. 값이 더 많은 축은
// 막대 길이로 비교하는 편이 정확해서 `CountBarChart` 를 쓴다.
//
// 색은 값의 고정 vocab 순서로 정한다(호출부가 정한다). 이 컴포넌트는 받은 순서대로 칠하고
// 다시 정렬하지 않는다. 링은 그림이고 값의 기준은 범례다. 색이 유일한 채널이 되지 않도록
// 범례가 문구·개수·비율을 함께 적는다.
export function ShareDonutChart({ segments, title, formatCount }: ShareDonutChartProps) {
  const summary = segments.map((segment) => segmentTitle(segment, formatCount)).join(", ");
  return (
    <div className={styles.axis}>
      <span className={styles.title}>{title}</span>
      {/* 링 내부는 role="img" 아래라 보조기기에 조각별로 노출되지 않는다. 요약 한 문장이 전체를
          전달한다. */}
      <div className={styles.ring} role="img" aria-label={`${title}: ${summary}`}>
        <ResponsiveContainer width="100%" height={RING_HEIGHT}>
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Tooltip
              formatter={(value: number, name: string) => {
                // 툴팁에도 비율을 넣는다. 축 안에서 라벨이 유일하므로 라벨로 조각을 찾는다.
                const segment = segments.find((item) => item.label === name);
                const share = segment === undefined ? "" : ` · ${percentText(segment.share)}`;
                return [`${formatCount(value)}${share}`, name];
              }}
              contentStyle={{
                background: CHART_CHROME.tooltipBg,
                border: `1px solid ${CHART_CHROME.tooltipBorder}`,
                borderRadius: MARK.radius,
                color: CHART_CHROME.tooltipInk,
                fontSize: MARK.tickFontSize,
              }}
              itemStyle={{ color: CHART_CHROME.tooltipInk }}
            />
            <Pie
              data={[...segments]}
              dataKey="count"
              nameKey="label"
              innerRadius="60%"
              outerRadius="90%"
              startAngle={90}
              endAngle={-270}
              // 인접 조각 사이 배경 간격. MARK.gap(px)과 뜻은 같지만 단위가 각도(°)라 따로 둔다.
              paddingAngle={2}
              isAnimationActive={false}
            >
              {segments.map((segment) => (
                <Cell key={segment.key} fill={segment.color} stroke="none" />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className={styles.legend}>
        {segments.map((segment) => (
          <li key={segment.key} className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: segment.color }} />
            <span className={styles.legendLabel}>{segment.label}</span>
            <span className={styles.legendValue}>
              {formatCount(segment.count)} · {percentText(segment.share)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
